import { useState } from "react";
import { dateDiff } from "~/helpers/date";
import {
  buildWorkflowDateChain,
  getDateChainBounds,
  getStepDateIssues,
  type DateChainBounds,
  type StepDateIssue,
  type WorkflowDateKey,
} from "~/helpers/workflowDates";
import { useBulkHoverHighlight } from "~/hooks/useBulkHoverHighlight";
import { useBulkSelection } from "~/hooks/useBulkSelection";

export interface WorkflowDates {
  reviewFrom: Date | null;
  reviewTo: Date | null;
  stepDueDates: (Date | null)[];
}

export function useWorkflowDatesEditor(
  rows: {
    id: number;
    reviewFrom: Date | null;
    reviewTo: Date | null;
    stepDueDates?: (Date | null)[];
    lockedFields?: WorkflowDateKey[];
  }[],
  stepCount: number,
) {
  const [dates, setDates] = useState<Map<number, WorkflowDates>>(() => {
    const initial = new Map<number, WorkflowDates>();
    for (const row of rows) {
      initial.set(row.id, {
        reviewFrom: row.reviewFrom,
        reviewTo: row.reviewTo,
        stepDueDates: Array.from(
          { length: stepCount },
          (_, index) => row.stepDueDates?.[index] ?? null,
        ),
      });
    }
    return initial;
  });

  const lockedFieldsById = new Map(
    rows.map((row) => [row.id, row.lockedFields]),
  );
  const isLocked = (rowId: number, field: WorkflowDateKey) =>
    lockedFieldsById.get(rowId)?.includes(field) ?? false;

  const canBulkEdit = rows.length > 1;

  const { selectedIds, allSelected, toggleRow, toggleAll, bulkTargets } =
    useBulkSelection(rows.map((row) => row.id));

  const { onHover, onHoverLeave, isHighlighted } = useBulkHoverHighlight<
    number,
    WorkflowDateKey
  >(selectedIds);

  // Editing a cell of a selected row edits that column for the whole selection.
  const updateCell = (
    rowId: number,
    field: WorkflowDateKey,
    value: Date | null,
  ) => {
    setDates((prev) => {
      const next = new Map(prev);

      for (const targetId of bulkTargets(rowId)) {
        const rowDates = next.get(targetId);
        if (!rowDates || isLocked(targetId, field)) continue;

        if (typeof field === "number") {
          const stepDueDates = [...rowDates.stepDueDates];
          stepDueDates[field] = value;
          next.set(targetId, { ...rowDates, stepDueDates });
        } else {
          next.set(targetId, { ...rowDates, [field]: value });
        }
      }

      return next;
    });
  };

  const issues = new Map<number, Map<number, StepDateIssue>>();
  const bounds = new Map<number, Map<WorkflowDateKey, DateChainBounds>>();
  const reversedWindows = new Set<number>();

  for (const [rowId, rowDates] of dates) {
    const chain = buildWorkflowDateChain(
      rowDates.stepDueDates.map((date, index) => ({
        dueAt: isLocked(rowId, index) ? null : date,
      })),
      rowDates.reviewFrom,
      rowDates.reviewTo,
    );

    const rowIssues = getStepDateIssues(chain);
    if (rowIssues.size > 0) issues.set(rowId, rowIssues);
    bounds.set(rowId, getDateChainBounds(chain));

    if (
      rowDates.reviewFrom &&
      rowDates.reviewTo &&
      dateDiff(rowDates.reviewTo, rowDates.reviewFrom, "day") < 0
    ) {
      reversedWindows.add(rowId);
    }
  }

  return {
    dates,
    issues,
    bounds,
    reversedWindows,
    hasErrors: issues.size > 0 || reversedWindows.size > 0,
    isLocked,
    canBulkEdit,
    updateCell,
    selectedIds,
    allSelected,
    toggleRow,
    toggleAll,
    onHover,
    onHoverLeave,
    isHighlighted,
  };
}

export type WorkflowDatesEditor = ReturnType<typeof useWorkflowDatesEditor>;
